import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { consumerStepPath, RETURN_TO_PARAM } from '../lib/paths';
import { CONSUMER_STEPS, isConsumerStep, type ConsumerStepId } from '../lib/steps';

/**
 * Moves through the flow by URL, so every screen has its own browser history entry. Side
 * trips (adjusting the scenario, comparing terms) carry a `returnTo` step and come back to it
 * when finished. Back always goes to the screen before this one in the flow, even when the
 * consumer resumed or reloaded part-way through and has no browser history for it.
 */
export function useConsumerFlow(clientId: string) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnToParam = searchParams.get(RETURN_TO_PARAM);
  const returnTo = isConsumerStep(returnToParam) ? returnToParam : null;

  const goTo = useCallback(
    (
      step: ConsumerStepId,
      options: {
        returnTo?: ConsumerStepId;
        replace?: boolean;
        state?: Record<string, unknown>;
      } = {},
    ) =>
      navigate(consumerStepPath(clientId, step, options.returnTo), {
        replace: options.replace,
        state: options.state,
      }),
    [clientId, navigate],
  );

  // A side trip keeps its return point while it moves between its own screens.
  const goWithin = useCallback(
    (step: ConsumerStepId) => goTo(step, { returnTo: returnTo ?? undefined }),
    [goTo, returnTo],
  );

  const finish = useCallback(
    (fallback: ConsumerStepId, options: { replace?: boolean } = {}) =>
      goTo(returnTo ?? fallback, { replace: options.replace }),
    [goTo, returnTo],
  );

  // Side-trip screens aren't resumable, so going back into one keeps the trip's return point.
  const back = useCallback(
    (previous: ConsumerStepId) =>
      goTo(previous, {
        returnTo: CONSUMER_STEPS[previous].resumable ? undefined : (returnTo ?? undefined),
      }),
    [goTo, returnTo],
  );

  return { goTo, goWithin, finish, back, returnTo };
}
